import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAddTeamMutation,
    useDeleteTeamMutation,
    useEditTeamMutation,
    useGetTeamListQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import ConfirmationModal from '../../shared/ConfirmationModal';
import toast from '../../shared/Toast';
import { Team } from './TeamList';
import { capitalizeName, handleHttpError } from '../../../shared/utils';

interface Props {
    team?: Team;
}

const TeamsForm = ({ team }: Props) => {
    const [isDuplicateName, setDuplicateName] = useState(false);
    const [addNewTeam] = useAddTeamMutation();
    const [teamName, setTeamName] = useState('');
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const [editTeam] = useEditTeamMutation();
    const [deleteTeam] = useDeleteTeamMutation();
    const { data: getTeams } = useGetTeamListQuery(loggedInUser?.company);
    const theme = useTheme();

    const [showModal, setShowModal] = useState(false);
    const [editingTeam, setEditingTeam] = useState(false);

    const teamNames = getTeams?.map((team: Team) => team?.name);

    const handleTeamName = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextName = event.target.value.toLowerCase();
        const findDuplicate = teamNames?.find(
            (name: string) => name === nextName && name !== team?.name,
        );
        if (findDuplicate !== undefined) {
            setDuplicateName(true);
            setTeamName('');
        } else {
            setDuplicateName(false);
            setTeamName(nextName);
        }
    };

    const errors: any = [];

    if (teamName.length === 0) {
        errors.push('Name cannot be empty');
    }

    useEffect(() => {
        if (team && team.name) {
            setTeamName(team.name);
        }
    }, [team]);

    const saveTeam = () => {
        if (team) {
            editTeam([team.id, { name: teamName }]).then((data: any) => {
                if (data) {
                    if (data?.error) {
                        const errorMessage = handleHttpError(data?.error);
                        toast.error(errorMessage);
                    } else {
                        toast.success(
                            `Team: ${teamName} has been successfully updated`,
                        );
                        setEditingTeam(false);
                    }
                } else {
                    toast.error(`Failed to edit team: ${teamName}`);
                }
            });
        } else {
            if (
                loggedInUser?.role === 'guest_company' ||
                loggedInUser?.role === 'guest_admin'
            ) {
                addNewTeam([loggedInUser?.company, { name: teamName }]).then(
                    (data: any) => {
                        if (data?.error) {
                            const errorMessage = handleHttpError(data?.error);
                            toast.error(errorMessage);
                        } else {
                            toast.success(
                                `New Team: ${teamName} has been created`,
                            );
                            navigate('/teams');
                        }
                    },
                );
            } else {
                toast.error(`You don't have the necessary rights to add teams`);
            }
        }
    };

    const handleCancelTeamEdit = () => {
        setDuplicateName(false);
        setTeamName(team?.name ?? '');
        setEditingTeam(false);
    };

    const handleDeleteTeam = () => {
        deleteTeam([team?.id]).then((data: any) => {
            if (data.error) {
                toast.error(
                    `Error: ${
                        teamName?.charAt(0)?.toUpperCase() + teamName.slice(1)
                    } ${data.error.data.message}`,
                );
            } else {
                toast.success(
                    `Team: ${
                        teamName?.charAt(0)?.toUpperCase() + teamName.slice(1)
                    } has been deleted`,
                );

                navigate('/teams');
            }
        });
    };

    const teamNameField = (
        <TextField
            fullWidth
            error={isDuplicateName}
            label={isDuplicateName === true ? 'Error: Team already exists' : ''}
            value={!isDuplicateName ? teamName : null}
            InputLabelProps={{
                shrink: true,
            }}
            onChange={handleTeamName}
            sx={{
                flex: { sm: '1 1 240px' },
                maxWidth: { sm: 400 },
                minWidth: 0,
                width: '100%',
            }}
            inputProps={{
                'aria-label': 'Team name',
                style: {
                    height: '10px',
                    backgroundColor: theme.palette.neutral.main,
                },
            }}
        />
    );

    return (
        <Grid
            sx={{
                width: '100%',
                minWidth: 0,
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: team ? '16px' : '40px',
                mb: team ? 4 : 0,
            }}
        >
            {team ? (
                <Tooltip title='Delete team'>
                    <IconButton
                        id='deleteTeam'
                        size='small'
                        type='button'
                        aria-label='Delete'
                        onClick={() => setShowModal(true)}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            color: 'text.secondary',
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </Tooltip>
            ) : null}
            {team ? (
                <ConfirmationModal
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    onChange={handleDeleteTeam}
                    message={'delete this team'}
                />
            ) : null}
            {team ? (
                <Stack spacing={1} sx={{ width: '100%' }}>
                    <Stack direction='row' spacing={0.5} alignItems='center'>
                        <Typography
                            component='h2'
                            sx={{ fontSize: '16px', fontWeight: 700 }}
                        >
                            Name
                        </Typography>
                        {!editingTeam ? (
                            <IconButton
                                size='small'
                                type='button'
                                aria-label='Edit team'
                                onClick={() => setEditingTeam(true)}
                            >
                                <EditIcon
                                    sx={{
                                        fontSize: 18,
                                        color: 'text.secondary',
                                    }}
                                />
                            </IconButton>
                        ) : null}
                    </Stack>
                    {editingTeam ? (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                        >
                            {teamNameField}
                            <Stack direction='row' spacing={1}>
                                <CustomButton
                                    onClick={handleCancelTeamEdit}
                                    sx={{
                                        fontSize:
                                            isMobile || isIpad
                                                ? '12px'
                                                : '14px',
                                    }}
                                >
                                    Cancel
                                </CustomButton>
                                <CustomButton
                                    id='saveTeam'
                                    variant='contained'
                                    color='primary'
                                    onClick={saveTeam}
                                    disabled={
                                        errors.length > 0 ||
                                        teamName === team.name
                                    }
                                    sx={{
                                        fontSize:
                                            isMobile || isIpad
                                                ? '12px'
                                                : '14px',
                                    }}
                                >
                                    Save
                                </CustomButton>
                            </Stack>
                        </Stack>
                    ) : (
                        <Typography>{capitalizeName(team.name)}</Typography>
                    )}
                </Stack>
            ) : (
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    New Team
                </Typography>
            )}
            {!team ? (
                <Stack
                    spacing={1.5}
                    sx={{ width: '100%', maxWidth: 480, mb: 5 }}
                >
                    <Typography>
                        Team Name
                        {teamName === '' ? (
                            <span style={{ color: 'red' }}>*</span>
                        ) : null}
                    </Typography>
                    {teamNameField}
                    <CustomButton
                        id='saveTeam'
                        fullWidth={isMobile || isIpad}
                        variant='contained'
                        color='primary'
                        onClick={saveTeam}
                        disabled={errors.length > 0}
                        sx={{
                            alignSelf: {
                                xs: 'stretch',
                                sm: 'flex-start',
                            },
                            fontSize: isMobile || isIpad ? '12px' : '14px',
                        }}
                    >
                        Save
                    </CustomButton>
                </Stack>
            ) : null}
        </Grid>
    );
};

export default TeamsForm;
